import { useState } from 'react';
import { complianceApi, type EWayBillRecord } from '@/api/compliance.api';
import { parseEWBErrors } from '@/utils/ewbErrorCodes';
import { useToast } from '@/contexts/ToastContext';

interface Props {
  ewb: EWayBillRecord;
  onClose: () => void;
  onUpdated: () => void;
}

export default function UpdateTransporterModal({ ewb, onClose, onUpdated }: Props) {
  const toast = useToast();

  const [transporterId, setTransporterId]     = useState(ewb.transporterId || '');
  const [transporterName, setTransporterName] = useState(ewb.transporterName || '');
  const [loading, setLoading]                 = useState(false);

  const canSubmit = Boolean(
    ewb.ewbNumber &&
    transporterId.trim() &&
    transporterId.trim().length >= 15
  );

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setLoading(true);

    try {
      const payload = {
        ewbId: ewb.id,
        ewbNo: ewb.ewbNumber,
        transporterId: transporterId.trim().toUpperCase(),
        transporterName: transporterName.trim() || undefined,
      };

      const res = await complianceApi.updateTransporter(payload);

      if (res?.success) {
        toast.success(res.message || 'Transporter updated successfully ✓');
        onUpdated();
        onClose();
      } else {
        const parsedErr = parseEWBErrors(res?.errorDetails || res?.message);
        if (parsedErr?.errors?.length > 0) {
          parsedErr.errors.forEach(err => {
            toast.error(err.fix ? `${err.message} — ${err.fix}` : err.message);
          });
        } else {
          toast.error(parsedErr.userMessage || res?.message || 'Failed to update transporter');
        }
      }
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update transporter');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="bg-slate-900 text-white px-6 py-4 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-500 text-slate-900 uppercase tracking-wider">
                Transporter
              </span>
              <h3 className="text-base font-semibold">Update Transporter</h3>
            </div>
            <p className="text-xs text-slate-400 mt-0.5 font-mono">
              EWB #{ewb.ewbNumber}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          {/* Current Transporter Info */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-3.5 space-y-1.5 text-xs">
            <div className="flex items-center justify-between text-slate-600">
              <span>Current Transporter ID:</span>
              <span className="font-mono font-bold text-slate-900">{ewb.transporterId || 'None Assigned'}</span>
            </div>
            {ewb.transporterName && (
              <div className="flex items-center justify-between text-slate-600">
                <span>Current Name:</span>
                <span className="font-semibold text-slate-900">{ewb.transporterName}</span>
              </div>
            )}
          </div>

          {/* New Transporter ID */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
                New Transporter ID / GSTIN <span className="text-red-500">*</span>
              </label>
              <span className="text-[11px] text-slate-400 font-mono">15 digits</span>
            </div>
            <input
              type="text"
              value={transporterId}
              onChange={(e) => setTransporterId(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
              placeholder="e.g. 29AAGCB1286Q000"
              maxLength={15}
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 uppercase font-mono font-medium"
            />
            <p className="mt-1 text-[11px] text-slate-400">
              15-character GSTIN or Transporter ID (TRANSIN) registered on the GST Portal.
            </p>
          </div>

          {/* New Transporter Name */}
          <div>
            <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
              Transporter Name <span className="text-slate-400 font-normal">(Optional)</span>
            </label>
            <input
              type="text"
              value={transporterName}
              onChange={(e) => setTransporterName(e.target.value)}
              placeholder="e.g. VRL Logistics Ltd."
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-3 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 cursor-pointer transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit || loading}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer flex items-center justify-center gap-2 shadow-xs transition-colors"
            >
              {loading ? (
                <>
                  <i className="ri-loader-4-line animate-spin" />
                  Updating...
                </>
              ) : (
                <>
                  <i className="ri-truck-line" />
                  Update Transporter
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
